import { AxiosWithCancelGet } from '@@types/Api';
import { ContentsWrap, ContentWrap, RawWrap, StringTMap } from '@@types/common';

import { HTTP_CLIENT_HEADER, HTTP_CLIENT_IP } from '~/constants';
import { DEFAULT_TIMEOUT } from '~/constants/api';
import performanceData from '~/stores/data/performanceData';
import { eitherBy } from '~/utils/fnUtil/fnUtil';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { get, isEmpty } from 'lodash';
import Qs from 'qs';
import { assoc, dissoc, path, pipe, prop } from 'ramda';
import { generate } from 'short-uuid';

export const isSuccessful = <V>({ status, data }: AxiosResponse<V>): boolean => {
  const is200 = status >= 200 && status < 300;
  return is200 && (isEmpty(get(data, 'header', {})) || get(data, 'header.isSuccessful', false));
};

export const getData: <R>(value: AxiosResponse<R>) => R = prop('data');

export const getResult: <R>(value: AxiosResponse<RawWrap<R>>) => R = path(['data', 'result']);

export const getContents: <V>(value: AxiosResponse<RawWrap<ContentsWrap<V> | ContentWrap<V>>>) => V = pipe(
  getResult,
  eitherBy(prop('content'), prop('contents'))
);

export const handleError = (xhrErrorHandler, error: any, ignore = false) => {
  if (ignore) {
    return Promise.reject(error);
  }

  if (error.handled) {
    return Promise.reject(dissoc('handled', error));
  } else {
    xhrErrorHandler(error);
    return Promise.reject(assoc('handled', true, error));
  }
};

export const createRequester = (
  errorHandler,
  ipHandler,
  { unWrapper = getData, ignoreError = false }: { unWrapper?: (response: AxiosResponse) => any; ignoreError?: boolean }
): AxiosWithCancelGet => {
  const requester = axios.create({
    timeout: DEFAULT_TIMEOUT,
    paramsSerializer: (params) => Qs.stringify(params, { arrayFormat: 'comma' }),
  });
  requester.interceptors.request.use((config) => {
    const token = `${generate()}-${Date.now()}`;
    config.headers[HTTP_CLIENT_HEADER] = token;
    performanceData[token] = performance.now();
    return config;
  });

  requester.interceptors.response.use((res) => {
    const ip = res.headers[HTTP_CLIENT_IP];
    if (ip) {
      ipHandler(ip);
    }
    if (!ignoreError) {
      if (isSuccessful(res)) {
        const token = res.config.headers[HTTP_CLIENT_HEADER];
        delete performanceData[token];
        return unWrapper(res);
      } else {
        return handleError(errorHandler, res);
      }
    }
    return res;
  });
  return createRequesterWithOptions(requester, errorHandler, ignoreError);
};

export const createRequesterWithOptions = (requester, errorHandler: () => void, ignoreError: boolean): any => {
  const cancelMap: StringTMap<{ cancel: (message: string) => void }> = {};
  return {
    delete: (url: string, options?: AxiosRequestConfig) => {
      return requester.delete(url, options).catch((error) => handleError(errorHandler, error, ignoreError));
    },
    get: (url: string, options?: AxiosRequestConfig) => {
      return requester.get(url, options).catch((error) => handleError(errorHandler, error, ignoreError));
    },
    cancelGet: makeCancelRequest('get'),
    head: (url: string, options?: AxiosRequestConfig) => {
      return requester.head(url, options);
    },
    post: (url: string, body?, options?: AxiosRequestConfig) => {
      return requester.post(url, body, options).catch((error) => handleError(errorHandler, error, ignoreError));
    },
    cancelPost: makeCancelRequest('post'),
    put: (url: string, body?, options?: AxiosRequestConfig) => {
      return requester.put(url, body, options).catch((error) => handleError(errorHandler, error, ignoreError));
    },
  };

  function makeCancelRequest(method: 'get' | 'post') {
    return method === 'get'
      ? (url: string, diffInUrl: string, options: AxiosRequestConfig = {}) => {
          const cancelToken = cancelAndGetToken(url, diffInUrl);
          return requester
            .get(url, { cancelToken, ...options })
            .catch((error) => handleError(errorHandler, error, ignoreError));
        }
      : (url: string, body: any, diffInUrl: string, options: AxiosRequestConfig = {}) => {
          const cancelToken = cancelAndGetToken(url, diffInUrl);
          return requester
            .post(url, body, { cancelToken, ...options })
            .catch((error) => handleError(errorHandler, error, ignoreError));
        };
  }

  function cancelAndGetToken(url: string, diffInUrl: string) {
    const key = url.replace(diffInUrl, '');
    cancelMap[key]?.cancel(url);
    const cancelSource = makeCancelSource();
    cancelMap[key] = cancelSource;
    return cancelSource.token;
  }
};

function makeCancelSource() {
  return axios.CancelToken.source();
}
